import {ChangeDetectionStrategy, Component, computed, inject, input, model, Signal} from '@angular/core';
import {
  CancelVisit,
  DeclarationType,
  EmergencyProcedureBerthVisitSummary,
  EmergencyProcedureSummary,
  Mooring,
  RequiredQuantity,
  SummaryAccessPoint,
  TaskMessageStatus,
  VisitSummaryStatus
} from "@portbase/bezoekschip-service-typescriptmodels";
import {AppContext} from "../../../app-context";
import {DeclarationMessageStatus} from "../../visit-overview/visit-overview-item/visit-overview.utils";
import {EmergencyOverviewUtils, EmergencySummaryWithDummyfields} from "../emergency-overview.utils";
import {openConfirmationModalWithCallback, openEditModal, sendCommand} from "../../../common/utils";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../../common/modal/modal-confirm.component";
import {PortvisitUtils} from "../../../refdata/portvisit-utils";
import {OperationalVisitEditorComponent} from '../operational-visit-editor/operational-visit-editor.component';
import {EmergencyOverviewColumnService} from '../emergency-overview-column.service';

@Component({
  selector: 'app-emergency-overview-item',
  templateUrl: './emergency-overview-item.component.html',
  styleUrls: ['./emergency-overview-item.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmergencyOverviewItemComponent {

  readonly columnService = inject(EmergencyOverviewColumnService);

  /** Set of visible column field names for fast template checks */
  readonly visibleFields = computed(() =>
    new Set(this.columnService.visibleColumns().map(col => col.field))
  );

  utils = EmergencyOverviewUtils;
  appContext = AppContext;
  portVisitUtils = PortvisitUtils;

  declarationTypesHarbourMaster: DeclarationType[] = [/*DeclarationType.HARBOUR_MASTER*/];
  declarationTypesLoodswezen: DeclarationType[] = [/*DeclarationType.LOODSWEZEN*/];
  declarationTypesOther: DeclarationType[] = [DeclarationType.VISIT, DeclarationType.HEALTH, DeclarationType.SECURITY, DeclarationType.WASTE, DeclarationType.DANGEROUS_GOODS];

  emergencySummary = model<EmergencySummaryExpanded>(undefined, {alias: 'summary'});
  term = input<string>();

  expected = computed(() => [
    VisitSummaryStatus.DRAFT, VisitSummaryStatus.PLANNED, VisitSummaryStatus.EXPECTED
  ].includes(this.emergencySummary().visitStatus));

  areNauticalServicesApplicable = computed(() =>
    this.portVisitUtils.areNauticalServicesApplicable(this.emergencySummary().portUnCode));

  // Draught shown for the movement. Prefer the harbour master's actual draught
  // (the value edited in the emergency procedure editor), fall back to the
  // agent-reported draught, then to the vessel's max draught.
  vesselDraught = computed<number | undefined>(() =>
    EmergencyOverviewUtils.getActualVesselDraught(this.emergencySummary())
    ?? EmergencyOverviewUtils.getAgentReportedVesselDraught(this.emergencySummary())
    ?? this.emergencySummary().vessel?.maxDraught
    ?? undefined);

  // Left status set: harbour master + loodswezen (pilotage).
  // TODO: `harbourMasterStatus`/`loodswezenStatus` are mock-only dummy fields. Once the real
  //  HARBOUR_MASTER/LOODSWEZEN declaration types exist, drive these from getTaskStatuses instead.
  harbourMasterStatuses = computed<DeclarationMessageStatus[]>(() => [{
    name: "Harbour master",
    taskStatus: (this.emergencySummary().harbourMasterStatus as TaskMessageStatus) ?? TaskMessageStatus.UNKNOWN,
    messages: []
  }]);

  loodswezenStatuses = computed<DeclarationMessageStatus[]>(() => [{
    name: "Loodswezen",
    taskStatus: (this.emergencySummary().loodswezenStatus as TaskMessageStatus) ?? TaskMessageStatus.UNKNOWN,
    messages: []
  }]);

  isOrganisationCurrentVisitDeclarant = computed(() => {
    return AppContext.isAdmin()
      || this.emergencySummary().declarantShortName === AppContext.userProfile.organisation?.shortName
      || this.emergencySummary().ownerShortName === AppContext.userProfile.organisation?.shortName;
  });

  navigationUrl = computed(() => {
    if (this.emergencySummary().oldVisit) {
      const environmentMatch = window.location.href.match(/https:\/\/portvisit\.(.*)\.portbase\.com.*/);
      if (environmentMatch && environmentMatch[1] === 'pcs') {
        return 'https://vesselcall.' + AppContext.environment +
          '.portbase.com/beta/index.html#!/details/' + this.emergencySummary().crn + '/portauthorityDetails';
      } else {
        return 'https://vesselcall.' + AppContext.environment +
          '.portbase.com/#!/details/' + this.emergencySummary().crn + '/portauthorityDetails';
      }
    } else {
      return '/details/' + this.emergencySummary().crn;
    }
  });

  cancelVisit = () => {
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed) {
        this.doCancelVisit();
      }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "danger",
      title: "Danger",
      message: "Once a visit is cancelled it is no longer possible to make changes.",
      question: "Are you sure that you want to cancel this visit?",
      confirmText: "Yes",
      cancelText: "No"
    }, 'static');
  }

  doCancelVisit = () => {
    sendCommand('com.portbase.bezoekschip.common.api.visit.CancelVisit', <CancelVisit>{
      crn: this.emergencySummary().crn
    }, () => {
      this.emergencySummary.update(summary => {
        return {
          ...summary,
          cancelled: true,
          visitStatus: VisitSummaryStatus.CANCELLED
        };
      });
      AppContext.registerSuccess('The visit was cancelled successfully.');
    });
  }

  sinceLastUpdate = (lastUpdated?: string): string => {
    if (!lastUpdated) {
      return "";
    }

    const diffMinutes = Math.floor(
      (Date.now() - new Date(lastUpdated).getTime()) / 60000
    );

    if (!Number.isFinite(diffMinutes) || diffMinutes <= 0) {
      return "";
    }

    if (diffMinutes < 60) {
      return `${diffMinutes} min`;
    }

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    return minutes > 0
      ? `${hours} hr ${minutes} min`
      : `${hours} hr`;
  };

  togglePin(): void {
    this.emergencySummary.update(summary => {
      summary.pinned = !summary.pinned
      return summary;
    });
  }

  openEditor(): void {
    openEditModal(OperationalVisitEditorComponent, {
      summary: this.emergencySummary(),
    });
  }

  getNumberOfTugboats = (requiredQuantity?: RequiredQuantity): number | string => {
    switch (requiredQuantity) {
      case RequiredQuantity.ONE:
        return 1;
      case RequiredQuantity.TWO:
        return 2;
      case RequiredQuantity.THREE:
        return 3;
      case RequiredQuantity.FOUR:
        return 4;
      case RequiredQuantity.FIVE:
        return 5;
      case RequiredQuantity.SIX:
        return 6;
      case RequiredQuantity.SEVEN:
        return 7;
      case RequiredQuantity.EIGHT:
        return 8;
      case RequiredQuantity.NINE:
        return 9;
      case RequiredQuantity.PILOT_DETERMINES:
        return "LAB";
      default:
        return null;
    }
  }

  // Short nautical mooring codes as shown in the design:
  //  BB = bakboord (port side), SB = stuurboord (starboard side),
  //  GV = geen voorkeur (no preference).
  // TODO: LZ (langszij) for ALONGSIDE_OTHER_SHIP is a best-guess abbreviation —
  //  there is no canonical short code for it elsewhere in the codebase yet.
  private mooringAbbreviation = (mooring?: Mooring): string | undefined => {
    switch (mooring) {
      case Mooring.PORT_SIDE:
        return "BB";
      case Mooring.STARBOARD_SIDE:
        return "SB";
      case Mooring.NO_PREFERENCE:
        return "GV";
      case Mooring.ALONGSIDE_OTHER_SHIP:
        return "LZ";
      default:
        return undefined;
    }
  }

  originInfo: Signal<{ time: string, name: string }> = computed(() => {
    const summary = this.emergencySummary();
    const originPlace = summary.movementOriginPlace;
    if (isAccessPoint(originPlace)) {
      return {
        time: EmergencyOverviewUtils.getMovementOriginTime(summary),
        // TODO: this must always be set
        name: summary.pilotStation?.name ?? '',
      };
    }

    return {
      time: EmergencyOverviewUtils.getMovementOriginTime(summary),
      name: originPlace.name,
    }
  });

  targetInfo: Signal<{ time: string, name: string, extra?: string }> = computed(() => {
    const summary = this.emergencySummary();
    const targetPlace = summary.movementTargetPlace;
    if (isAccessPoint(targetPlace)) {
      return {
        time: EmergencyOverviewUtils.getMovementTargetTime(summary),
        // TODO: this must always be set
        name: summary.exitPoint?.name ?? '',
      };
    }

    // Target place is a berth
    const bollardText = targetPlace.bollardTo && targetPlace.bollardFrom
      ? [targetPlace.bollardFrom, targetPlace.bollardTo].join("-")
      : undefined;
    const mooringAbbr = this.mooringAbbreviation(targetPlace.mooring);
    const mooringText = mooringAbbr ? `(${mooringAbbr})` : undefined;

    return {
      time: EmergencyOverviewUtils.getMovementTargetTime(summary),
      name: targetPlace.name,
      extra: [
        bollardText,
        mooringText
      ].filter(Boolean).join(" ")
    }
  });

  movementOnHold = computed<boolean>(() => {
    const summary = this.emergencySummary();
    const target = summary.movementTargetPlace;
    if (isAccessPoint(summary.movementOriginPlace) && isBerth(target)) {
      return summary.incomingMovementHarbourMasterInfo?.movementOnHold ?? false; // incoming
    }
    return isBerth(target) ? (target.harbourMasterInfo?.movementOnHold ?? false) : false; // shifting/outgoing
  });

  tryRefreshEmergencyProcedureSummary = () => {
    sendCommand('com.portbase.bezoekschip.common.api.emergencyprocedure.RefreshEmergencyProcedureSummary', {
      crn: this.emergencySummary().crn
    }, (summary: EmergencyProcedureSummary) => {
      this.emergencySummary.set(EmergencyOverviewUtils.toExtendedSummary(summary as EmergencySummaryWithDummyfields));
      AppContext.registerSuccess('Emergency procedure summary has been rebuild successfully.');
    });
  }

  protected readonly isAccessPoint = isAccessPoint;
  protected readonly isBerth = isBerth;
  protected readonly EmergencyOverviewUtils = EmergencyOverviewUtils;
}

export interface EmergencySummaryExpanded extends EmergencySummaryWithDummyfields {
  movementOriginPlace: MovementPlace;
  movementTargetPlace: MovementPlace;
}

// These types ensure the predicate checks below actually distinguish between berth and access point in the typesystem
export type MovementPlace =
  | (SummaryAccessPoint & { id?: undefined })
  | (EmergencyProcedureBerthVisitSummary & { id: string });

export const isBerth = (movementPlace: MovementPlace): movementPlace is (EmergencyProcedureBerthVisitSummary & { id: string }) => movementPlace['id'] !== undefined;
export const isAccessPoint = (movementPlace: MovementPlace): movementPlace is (SummaryAccessPoint & { id?: undefined }) => movementPlace['id'] === undefined;
